import React from 'react';
import { Table, Button, Space, Popconfirm, Pagination } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styles from './index.module.less';

export interface ActionButton<T = Record<string, unknown>> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: (record: T) => void;
  type?: 'link' | 'default' | 'primary';
  danger?: boolean;
  confirm?: {
    title: string;
    okText?: string;
    cancelText?: string;
  };
}

export interface DataTableProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  loading: boolean;
  rowKey: string;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  onPaginationChange: (page: number, pageSize?: number) => void;
  actionButtons?: ActionButton<T>[];
  className?: string;
}

function DataTable<T extends { [key: string]: unknown }>({
  columns,
  dataSource,
  loading,
  rowKey,
  pagination,
  onPaginationChange,
  actionButtons = [],
  className,
}: DataTableProps<T>) {
  // 构建操作列
  const actionColumn =
    actionButtons.length > 0
      ? {
          title: '操作',
          key: 'action',
          fixed: 'right' as const,
          width: 140,
          render: (_: unknown, record: T) => (
            <Space size='middle'>
              {actionButtons.map(button => {
                const buttonElement = (
                  <Button
                    key={button.key}
                    type={button.type || 'link'}
                    icon={button.icon}
                    danger={button.danger}
                    onClick={() => button.onClick(record)}
                  >
                    {button.label}
                  </Button>
                );

                // 如果需要确认，包装在Popconfirm中
                if (button.confirm) {
                  return (
                    <Popconfirm
                      key={button.key}
                      title={button.confirm.title}
                      onConfirm={() => button.onClick(record)}
                      okText={button.confirm.okText || '确定'}
                      cancelText={button.confirm.cancelText || '取消'}
                    >
                      <Button
                        key={button.key}
                        type={button.type || 'link'}
                        icon={button.icon}
                        danger={button.danger}
                        // 移除onClick，因为Popconfirm会处理点击事件
                      >
                        {button.label}
                      </Button>
                    </Popconfirm>
                  );
                }

                return buttonElement;
              })}
            </Space>
          ),
        }
      : null;

  // 合并列定义
  const finalColumns = actionColumn ? [...columns, actionColumn] : columns;

  return (
    <>
      <div className={`${styles.tableContainer} ${className || ''}`}>
        <Table
          columns={finalColumns}
          dataSource={dataSource}
          loading={loading}
          rowKey={rowKey}
          scroll={{ x: 'max-content' }}
          pagination={false}
        />
      </div>
      <div className={styles.paginationContainer}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger={true}
          showQuickJumper={true}
          showTotal={total => `共 ${total} 条记录`}
          onChange={onPaginationChange}
        />
      </div>
    </>
  );
}

export default DataTable;
